/**
 * THE OVERRIDE BUCKET (unified resolution surface §8 · U4/U5) — the model.
 * PURE and client-safe, like `cellState.ts`; the stored half is the caller's
 * (`useOverrides`) and the assembly happens in `flowState.ts`.
 *
 * Ported from the studio `src/core/overrides.ts` at the freeze. Adapted at two
 * seams and nowhere else:
 *
 *   1. **i18n.** The studio hardcodes English; here every reader-facing string is
 *      a translation key (identical key sets across EN/DE/JA), so the meta
 *      carries `*Key` fields rather than sentences.
 *   2. **Backing.** The studio's five dispositions each ride a store that already
 *      exists there. In this app four of them now do — the append-only
 *      `override_dispositions` store landed with the review gestures — so
 *      `notBackedKey` is null on all four, and the one that remains is `included`:
 *      there is no add-statement endpoint, so the capability is DECLARED in the
 *      bucket's footer rather than offered as a control that would do nothing. A
 *      gap is DECLARED, never quietly dropped, and a gap that has been CLOSED
 *      stops being marked — a stale "not saved" badge is its own kind of lie.
 *
 * Override is the fourth bucket. The first three are DERIVED STATE — what the
 * data currently is. This one is AUTHORSHIP: the human has taken over an item,
 * and in exchange the machine stops offering guidance it cannot honestly give.
 *
 * ── THE KINDS ────────────────────────────────────────────────────────────────
 *
 *   suppressed       a statement's scope is applied with unmatched references
 *                    outstanding: reviewed and accepted. Resolves ONLY the scope
 *                    decision (closes the statement's step-2 issue): its step-1
 *                    verdict and step-3 condition fields stay open, because
 *                    accepting WHICH articles a statement covers is not a
 *                    judgment on what the statement itself says (product
 *                    decision, TUR-1211 wave 5). Publishes nothing new.
 *   excluded         the statement is not processed at all — a DECLARED GAP.
 *   ignored          ONE ARTICLE of one statement cannot be settled from this
 *                    document. The same declared gap, one unit down; the only
 *                    ITEM-level kind, so the only one that uses `itemId`.
 *   included         a statement the pipeline never extracted, added by hand.
 *   proceed_unknown  go on with the verdict unknown; it stays unknown on record.
 *
 * There is deliberately **no `overridden` kind and no publish checkbox** (not
 * even a disabled one — §4 reserves grey for "blocked", and a permanently grey
 * control would lie). Recording a compliance STATUS on human authority stays
 * parked: the declaration record cannot yet tell an authored verdict apart from
 * one the pipeline determined. This is a settled decision on both sides.
 *
 * ── DISPOSITIONS, NEVER VALUES (U4) ──────────────────────────────────────────
 *
 * The store holds `(unit, unitId, itemId) → {kind, comment, createdAt, snapshot}`
 * and nothing else. Every VALUE keeps its existing writer (the statement verdict
 * edit, the per-statement scope, the condition fields), so nothing gets a second
 * source of truth.
 *
 * ── MEMBERSHIP IS DERIVED (U5) ───────────────────────────────────────────────
 *
 * None of the kinds is a new gesture. `suppressed` rides Apply-with-unmatched-
 * references (the per-statement scope endpoint); `excluded` rides the staged
 * statement review status (reject). The rest ride the NOTE plus a live guard: a
 * disposition in force exactly while its note stands and its condition holds
 * cannot disagree with itself.
 *
 * ── DIVERGENCE IS DERIVED, NOT AN ADVISORY ROW ───────────────────────────────
 *
 * The note records the facts it was written against. When those facts move, the
 * override is a YELLOW ITEM INSIDE THE BUCKET, never a separate advisory row. A
 * note with no snapshot cannot diverge — the same grandfathering idiom as a
 * verify mark with no confirmation record.
 */
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { CellState, WorklistIssue } from "./cellState"

// ── The kinds ────────────────────────────────────────────────────────────────

export type OverrideKind =
  | "suppressed"
  | "proceed_unknown"
  | "ignored"
  | "excluded"
  | "included"

/** Display order — the order the bucket lists them and the Finish count names
 *  them: least written first, and within that the smaller unit before the
 *  larger. */
export const OVERRIDE_KINDS: OverrideKind[] = [
  "suppressed",
  "proceed_unknown",
  "ignored",
  "excluded",
  "included",
]

export interface OverrideKindMeta {
  /** How the bucket names it. */
  labelKey: TranslationKeys
  /** How the DECLARATION names it — precise per item, never "override" (§8: the
   *  bucket name is UI shorthand, the declaration is not). */
  declarationKey: TranslationKeys
  /** THE ONE SENTENCE the override panel says — the gesture and what it costs,
   *  together, addressed to the person deciding. A kind that needed a second
   *  line to be honest would be a kind the reader cannot hold in their head. */
  sentenceKey: TranslationKeys
  /** Why this override wants a second look once its snapshot moved. */
  recheckKey: TranslationKeys
  /**
   * The honest mark this kind carries in THIS app, or null when the disposition
   * reaches a real store.
   *
   *   suppressed  → null. Apply writes the statement's scope (PUT
   *                 /statements/{id}/scope); the acceptance IS the gesture.
   *   excluded    → null. Rejecting the staged statement is persisted and takes it
   *                 out of what approve writes.
   *   proceed_unknown → null, as of the override store. The note is persisted at
   *                 `statement:<fw>:<gi>` and MEMBERSHIP is derived from it on the
   *                 server, which is why the flag is on the wire.
   *   ignored     → null for the note; see the ⚠ on `applyOverrides` for the one
   *                 thing that is still only half-backed (the server-side approve
   *                 gate does not read it).
   *   included    → the one genuinely unbacked kind: there is no add-statement
   *                 endpoint, so the capability is DECLARED rather than offered.
   *
   * The NOTE used to be a second question. It is not any more — see
   * `OVERRIDE_NOTE_NOT_BACKED`.
   */
  notBackedKey: TranslationKeys | null
}

export const OVERRIDE_KIND_META: Record<OverrideKind, OverrideKindMeta> = {
  suppressed: {
    labelKey: "MC_DECL_OV_SUPPRESSED",
    declarationKey: "MC_DECL_OV_SUPPRESSED_DECL",
    sentenceKey: "MC_DECL_OV_SUPPRESSED_SENTENCE",
    recheckKey: "MC_DECL_OV_SUPPRESSED_RECHECK",
    notBackedKey: null,
  },
  proceed_unknown: {
    labelKey: "MC_DECL_OV_PROCEED_UNKNOWN",
    declarationKey: "MC_DECL_OV_PROCEED_UNKNOWN_DECL",
    sentenceKey: "MC_DECL_OV_PROCEED_UNKNOWN_SENTENCE",
    recheckKey: "MC_DECL_OV_PROCEED_UNKNOWN_RECHECK",
    notBackedKey: null,
  },
  ignored: {
    labelKey: "MC_DECL_OV_IGNORED",
    declarationKey: "MC_DECL_OV_IGNORED_DECL",
    sentenceKey: "MC_DECL_OV_IGNORED_SENTENCE",
    recheckKey: "MC_DECL_OV_IGNORED_RECHECK",
    notBackedKey: null,
  },
  excluded: {
    labelKey: "MC_DECL_OV_EXCLUDED",
    declarationKey: "MC_DECL_OV_EXCLUDED_DECL",
    sentenceKey: "MC_DECL_OV_EXCLUDED_SENTENCE",
    recheckKey: "MC_DECL_OV_EXCLUDED_RECHECK",
    notBackedKey: null,
  },
  included: {
    labelKey: "MC_DECL_OV_INCLUDED",
    declarationKey: "MC_DECL_OV_INCLUDED_DECL",
    sentenceKey: "MC_DECL_OV_INCLUDED_SENTENCE",
    recheckKey: "MC_DECL_OV_INCLUDED_RECHECK",
    notBackedKey: "MC_DECL_NB_OV_INCLUDED",
  },
}

/**
 * The kinds this app can actually offer as a gesture. `included` is absent from
 * every option list because there is no add-statement endpoint to carry it — the
 * capability is DECLARED in the Override bucket's own footer rather than offered
 * as a control that would do nothing.
 */
export const OFFERABLE_OVERRIDE_KINDS: OverrideKind[] = OVERRIDE_KINDS.filter(
  (k) => k !== "included",
)

/**
 * THERE IS NO LONGER A NOTE GAP. The comment used to live for the browser session
 * only, and every surface that wrote one said so. It now lands in the append-only
 * override store (`POST /{id}/overrides`), keyed by address, with the author and
 * the timestamp the server stamped — and leaving an override RETIRES the row rather
 * than deleting it, so the history of a reviewer's changes of mind is the store.
 *
 * Kept as `null` rather than deleted so the one place that asked "is the note
 * backed?" still has an answer, and so this note explains where the mark went.
 */
export const OVERRIDE_NOTE_NOT_BACKED: TranslationKeys | null = null

/**
 * THE SERVER'S OWN REFUSAL, TRANSLATED — a 422 from `POST /{id}/overrides` carries
 * `{code, message}`, and until now every one of them rendered as the caller's generic
 * "could not record". That blind spot predates the two-tab work: the endpoint's most
 * likely refusal by far is `OVERRIDE_COMMENT_REQUIRED`
 * (`mc_declaration_review_service.py:550`), which is precisely actionable, and the
 * reader was told nothing. The four override call sites now read it back.
 *
 * The frontend's own `OVERRIDE_COMMENT_REQUIRED` constant is gone with this: the
 * server's code string is the one source, and `OverrideControl` keeps its blank-note
 * guard so this path is a backstop rather than the normal way a reader meets the
 * rule. Nothing else read the constant.
 *
 * ⚠ THE MANDATORY NOTE IS A DELIBERATE NON-PORT — see `OverrideControl.tsx`'s header
 * for the two backend places that would have to change first.
 */
export function overrideRefusalKey(e: unknown): TranslationKeys | null {
  const detail = (
    e as { response?: { data?: { detail?: { code?: unknown } } } } | null
  )?.response?.data?.detail
  return detail?.code === "OVERRIDE_COMMENT_REQUIRED"
    ? "MC_DECL_OV_NOTE_REQUIRED"
    : null
}

// ── The address ──────────────────────────────────────────────────────────────

/**
 * The store's address (U4). `itemId` is `""` for the unit itself; the column
 * exists because §8's items (an article, a reference) are the natural home of an
 * item-level disposition — and `ignored` is exactly that.
 */
export interface OverrideAddress {
  unit: "statement" | "run"
  /** The statement id, or `RUN_UNIT_ID`. */
  unitId: string
  itemId: string
}

export const RUN_UNIT_ID = "run"

export const overrideAddressKey = (a: OverrideAddress): string =>
  `${a.unit}|${a.unitId}|${a.itemId}`

export const statementOverrideAddress = (
  statementId: string,
): OverrideAddress => ({ unit: "statement", unitId: statementId, itemId: "" })

/** ONE ARTICLE OF ONE STATEMENT — the item-level address. A statement can carry
 *  a disposition of its own AND per-article ones at the same time, which is why
 *  every reader of "the statement's own override" asks for `itemId === ""`. */
export const articleOverrideAddress = (
  statementId: string,
  articleId: string,
): OverrideAddress => ({
  unit: "statement",
  unitId: statementId,
  itemId: articleId,
})

// ── The STORE's address, which is not this one ───────────────────────────────

/**
 * THE WIRE ADDRESS. The store keys a note by `statement:<framework>:<groupIndex>`,
 * `cell:<framework>:<articleNumber>` or `declaration` — the same address space the
 * graph and both classifier twins use. `OverrideAddress` above is the FLOW's own
 * key (a statement id, which is what the screens hold), so the two are converted
 * at the seam rather than one of them being bent to the other.
 *
 * Two different things, deliberately: a statement id is a staged-row identity that
 * says nothing about position, and the graph addresses by position because that is
 * what survives a re-extraction. Conflating them would make an override written
 * against one run's row ids unreadable by the next run's graph.
 */
export const statementStoreAddress = (
  framework: string,
  groupIndex: number,
): string => `statement:${framework}:${groupIndex}`

/** One CELL's address — the `ignored` kind's home in the store. */
export const cellStoreAddress = (
  framework: string,
  articleNumber: string,
): string => `cell:${framework}:${articleNumber}`

/** The whole declaration — the one unit above every framework. */
export const DECLARATION_STORE_ADDRESS = "declaration"

// ── The snapshot: the facts the note was written against ─────────────────────

/** A small normalized object, or null when nothing underneath the note can move
 *  (which reads as "cannot diverge", never as "unknown"). */
export type OverrideSnapshot = Record<string, unknown> | null

const sorted = (xs: string[]): string[] => [...new Set(xs)].sort()

/** A statement's suppression: the references outstanding when it was accepted,
 *  and the articles it applied. */
export const suppressedSnapshot = (i: {
  unmatched: string[]
  articles: string[]
}): OverrideSnapshot => ({
  unmatched: sorted(i.unmatched),
  articles: sorted(i.articles),
})

/** An exclusion: the identity that was excluded. */
export const excludedSnapshot = (i: {
  verdict: string | null
  listVersion: string | null
}): OverrideSnapshot => ({
  verdict: i.verdict ?? null,
  list_version: i.listVersion ?? null,
})

/** Proceeding as unknown: the identity it was decided over. The disposition
 *  retires by itself once the verdict stops reading unknown. */
export const proceedUnknownSnapshot = (i: {
  listVersion: string | null
}): OverrideSnapshot => ({
  verdict: "unknown",
  list_version: i.listVersion ?? null,
})

/** An ignored article: the cell's verdict, and whether the data that verdict
 *  requires was there. The second half is the one that matters — the declaration
 *  LATER saying it is exactly the fact that makes the note worth re-reading. */
export const ignoredSnapshot = (i: {
  verdict: string | null
  requiredComplete: boolean
}): OverrideSnapshot => ({
  verdict: i.verdict ?? null,
  required_complete: i.requiredComplete,
})

/** A hand-add: how many statements the PIPELINE had for that framework at the
 *  time. Unused today (there is no add-statement path) and kept so the model
 *  stays whole. */
export const includedSnapshot = (i: {
  pipelineGroups: number
}): OverrideSnapshot => ({ pipeline_groups: i.pipelineGroups })

// ── The note, and the view the surfaces render ───────────────────────────────

export interface OverrideNote {
  kind: OverrideKind
  comment: string
  createdAt: string
  snapshot: OverrideSnapshot
  /** Who recorded it. The SERVER's answer (it stamps the row from the request's
   *  own user), never the browser's guess — which is the whole reason an override
   *  is worth having in an audit at all. Null on a row that predates the column. */
  author?: string | null
}

/**
 * One member of the bucket: its derived membership, the note joined to it, and
 * whether the note's facts have moved. `comment: null` is a member recorded
 * before the note existed (or by a path that never asked) — grandfathered, and
 * said plainly rather than invented.
 */
export interface OverrideView extends OverrideAddress {
  kind: OverrideKind
  comment: string | null
  /** The note's timestamp — the comment travels stamped. */
  at: string | null
  /** The note's facts have moved: a yellow item inside the bucket (§8). */
  diverged: boolean
  /** Statement units only. */
  statementId?: string
  framework?: string
  groupIndex?: number
  /** The ITEM, when the disposition is item-level (`ignored`). */
  articleId?: string
  /** The app-wide `type · verdict` title, when the assembler knows it. */
  label?: string
  /** The `fw:articleNumber` cells this override is parked on — the matrix's
   *  authored glyph. EMPTY for a kind that publishes nothing at all (an
   *  exclusion has no cells to mark: the column stands with hollow cells, which
   *  is the honest reading of a declared gap). */
  cells: string[]
}

/** Has the note's world moved? A note with no snapshot cannot diverge. */
export function overrideDiverged(
  note: OverrideNote | null | undefined,
  current: OverrideSnapshot,
): boolean {
  if (!note || note.snapshot === null || current === null) return false
  return JSON.stringify(note.snapshot) !== JSON.stringify(current)
}

// ── Reading the bucket ───────────────────────────────────────────────────────

/** The one override in force on a statement ITSELF, if any — `itemId === ""`, so
 *  a per-article ignore parked under the same statement is never mistaken for a
 *  disposition on the whole of it. */
export function overrideForStatement(
  views: OverrideView[],
  statementId: string,
): OverrideView | null {
  return (
    views.find(
      (v) =>
        v.unit === "statement" && v.unitId === statementId && v.itemId === "",
    ) ?? null
  )
}

/** The ignore in force on one (statement × article), if any. */
export function overrideForArticle(
  views: OverrideView[],
  statementId: string,
  articleId: string,
): OverrideView | null {
  return (
    views.find(
      (v) =>
        v.unit === "statement" &&
        v.unitId === statementId &&
        v.itemId === articleId,
    ) ?? null
  )
}

/** Every article this statement has had ignored. */
export function ignoredArticlesOf(
  views: OverrideView[],
  statementId: string,
): string[] {
  return views
    .filter(
      (v) =>
        v.kind === "ignored" &&
        v.unit === "statement" &&
        v.unitId === statementId,
    )
    .map((v) => v.itemId)
}

/**
 * `fw:articleNumber` → the kind parked on it, for the matrix's authored glyph.
 * When two statements cover one cell and one of them is overridden, the override
 * wins: the human has taken over part of what that cell publishes, and the board
 * someone signs must say so.
 */
export function overrideCellMap(
  views: OverrideView[],
): Record<string, OverrideKind> {
  const out: Record<string, OverrideKind> = {}
  for (const v of views) for (const cell of v.cells) out[cell] = v.kind
  return out
}

export function countOverridesByKind(
  views: OverrideView[],
): Record<OverrideKind, number> {
  const out: Record<OverrideKind, number> = {
    suppressed: 0,
    proceed_unknown: 0,
    ignored: 0,
    excluded: 0,
    included: 0,
  }
  for (const v of views) out[v.kind]++
  return out
}

/**
 * The Finish-time tally, per kind (§8 · never silent). Structured rather than a
 * sentence so the three translation files own the wording. Null when there are
 * none, so the sign-off says nothing rather than "0 overrides".
 */
export function finishOverrideSummary(
  views: OverrideView[],
): { total: number; parts: { kind: OverrideKind; count: number }[] } | null {
  if (views.length === 0) return null
  const counts = countOverridesByKind(views)
  return {
    total: views.length,
    parts: OVERRIDE_KINDS.filter((k) => counts[k] > 0).map((k) => ({
      kind: k,
      count: counts[k],
    })),
  }
}

/** Why this override wants a second look — null when it does not. */
export function overrideRecheckKey(view: OverrideView): TranslationKeys | null {
  return view.diverged ? OVERRIDE_KIND_META[view.kind].recheckKey : null
}

/**
 * The state a card takes when an override is in force on it. The three derived
 * states classify the DATA; this one says who owns it — so it replaces them in
 * the bucket column rather than competing with them (§2: every item is in
 * exactly one bucket).
 */
export type BucketState = CellState | "override"

// ── The override layer over the derived worklist ─────────────────────────────

/**
 * THREE KINDS CLOSE WORK (§8), but not the same amount of it. `excluded` is a
 * DECLARED GAP over the whole statement, so the gesture closes every step.
 * `ignored` is the same declared gap one unit down: one article of one
 * statement's step-3 issue. `suppressed` closes ONLY the statement's step 2:
 * accepting a statement's scope while references stay unmatched is a decision
 * about WHICH ARTICLES it covers, not a judgment on its verdict (step 1) or its
 * condition fields (step 3), so those stay open exactly as the raw derivation
 * left them (product decision, TUR-1211 wave 5).
 *
 * This is deliberately a SEPARATE PASS over `deriveWorklist`'s output rather than
 * a branch inside it: `deriveWorklist` is pinned byte-for-byte against the
 * server-side Python twin, so only a fact BOTH sides read from the same context
 * may live inside it, and the `suppressed` / `ignored` / `excluded` memberships
 * are assembled here from notes and staged statuses that the shared context does
 * not carry.
 *
 * `proceed_unknown` USED TO BE THE FOURTH and no longer is. Its membership is now
 * a field on the wire (`GET /{id}/statements` ships the derivation per entry), so
 * both twins see the same answer and it belongs INSIDE the derivation, where
 * `classifyStatement` reads it. That is why the kind is absent below: it is not
 * dropped, it moved to where it can be true on both sides of the seam.
 *
 * The server runs the same pass: `derive_open_work` (behind approve, verify and
 * `/open-items`) applies `apply_overrides` after `derive_worklist`, closing an
 * excluded statement off its staged `rejected` status, a suppressed one off an
 * applied scope with unresolved references, and an ignored article off the notes in
 * force. `flowParity` pins this pass and its server twin to one shared fixture.
 */
export function applyOverrides(
  worklist: WorklistIssue[],
  views: OverrideView[],
): WorklistIssue[] {
  // `excluded` closes every step of the statement: the declared gap covers the
  // whole thing. `suppressed` closes ONLY step 2 (the scope decision the
  // accept-with-unmatched-refs gesture actually made); its step-1 and step-3
  // issues fall through to the ordinary per-step handling below.
  const closedAll = new Set(
    views
      .filter((v) => v.itemId === "" && v.kind === "excluded")
      .map((v) => v.unitId),
  )
  const closedStep2 = new Set(
    views
      .filter((v) => v.itemId === "" && v.kind === "suppressed")
      .map((v) => v.unitId),
  )
  const out: WorklistIssue[] = []
  for (const i of worklist) {
    // A framework unit has no statement to be overridden — it passes through.
    if (!i.statementId) {
      out.push(i)
      continue
    }
    if (closedAll.has(i.statementId)) continue
    if (i.step === 2 && closedStep2.has(i.statementId)) continue
    if (i.step !== 3) {
      out.push(i)
      continue
    }
    const ignored = new Set(ignoredArticlesOf(views, i.statementId))
    if (ignored.size === 0) {
      out.push(i)
      continue
    }
    const articleIds = i.articleIds.filter((a) => !ignored.has(a))
    if (articleIds.length === 0) continue
    out.push({ ...i, articleIds })
  }
  return out
}
